import { forwardRef, useMemo } from 'react';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { AccessPermissions } from 'core/enums';
import { FormProvider } from 'core/states';
import { Attributes, IAttributesHandle } from 'quotes/components/ExportSkuPart/steps/Attributes';
import {
  getPriorityModeFromConfig,
  getPriorityModeFromForm,
  mapAttributesToForm,
} from 'quotes/components/ExportSkuPart/steps/Attributes/mappers';
import { attributeSchema } from 'quotes/components/ExportSkuPart/steps/Attributes/schema';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IAttributes } from 'quotes/types';

import { applyFirstImportSelectableSpaDefault, seedSalesLocationsFromUserLocation } from './utils';

interface IProps {
  config: IAttributes | null;
  hasSubmitted: boolean;
  draft?: IAttributeForm | null;
  isFirstImport?: boolean;
}

export const ImportAttributes = forwardRef<IAttributesHandle, IProps>(function ImportAttributes(
  { config, hasSubmitted, draft = null, isFirstImport = false },
  ref
) {
  const { user } = useAuthUser();
  const userLocation = user?.location;
  const { hasPermission } = useUserPermissions();
  const canEnableSelectableSpa = hasPermission(AccessPermissions.SELECTABLE_SPA);

  const hydratedPriorityMode = useMemo(
    () => (draft ? getPriorityModeFromForm(draft) : getPriorityModeFromConfig(config)),
    [draft, config]
  );
  // Stable identity — resetDefaultValues re-runs reset() whenever this reference changes. Hence
  // the location primitives the seeding util reads rather than the auth user object: it gets a
  // fresh identity on every logged-user refetch, which would reset the wizard mid-edit.
  const defaultValues = useMemo(
    () =>
      draft ??
      applyFirstImportSelectableSpaDefault(
        seedSalesLocationsFromUserLocation(mapAttributesToForm(config), userLocation),
        { isFirstImport, canEnableSelectableSpa }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      draft,
      config,
      isFirstImport,
      canEnableSelectableSpa,
      userLocation?.id,
      userLocation?.isAlternateSales,
      userLocation?.name,
      userLocation?.externalLocationId,
      userLocation?.tradeAgreementOwner?.id,
    ]
  );

  return (
    <FormProvider
      schema={attributeSchema(
        null,
        VendorQuoteHelper.getSpaAudienceRights(user),
        hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA)
      )}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Attributes
        ref={ref}
        hasSubmitted={hasSubmitted}
        hydratedPriorityMode={hydratedPriorityMode}
      />
    </FormProvider>
  );
});
